import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import OnboardingLayout from '../../components/layouts/onboarding/OnboardingLayout';
import PrimaryButton from '../../components/common/PrimaryButton';
import {
  SoleTraderIcon,
  SmallBusinessIcon,
  EnterpriseIcon,
  CharityIcon
} from '../../components/icons/BusinessIcons';
import { colors, typography } from '../../styles/theme/index';
import {
  FormContainer,
  ButtonContainer
} from '../../styles/components';
import storage from '../../utils/storage';

const OrganisationTypeContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px 0 0;
`;

const OrganisationTypeBox = styled.div`
  background: ${props => props.selected ? colors.accent + '30' : colors.background.light};
  border: 1px solid ${props => props.selected ? colors.accent : colors.border};
  border-radius: 12px;
  padding: 16px 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: ${colors.accent}20;
    border-color: ${colors.accent};
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};
`;

const OrganisationLabel = styled.span`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  color: ${colors.text.primary};
  font-weight: ${typography.fontWeight.medium};
  flex: 1;
  margin-left: 16px;
`;

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" 
      fill={colors.primary}
    />
  </svg>
);

const IconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CheckIconWrapper = styled.div`
  opacity: ${props => props.visible ? 1 : 0};
  transform: ${props => props.visible ? 'scale(1)' : 'scale(0.8)'};
  transition: all 0.2s ease;
  margin-left: 16px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 32px;
  border-radius: 16px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h3`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.xl};
  color: ${colors.text.primary};
  margin: 0 0 16px;
  font-weight: ${typography.fontWeight.bold};
`;

const ModalText = styled.p`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  color: ${colors.text.secondary};
  margin: 0 0 24px;
  line-height: 1.5;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`;

const SecondaryButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.primary};
  background: transparent;
  border: 1px solid ${colors.border};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.light};
  }
`;

const WarningButton = styled(PrimaryButton)`
  background: ${colors.error};
  
  &:hover {
    background: ${colors.error}dd;
  }
`;

const OnboardingStep2 = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    // Check for required data from step 1
    const pipedriveData = storage.getPipedriveData();
    if (!pipedriveData?.personId || !pipedriveData?.dealId) {
      navigate('/onboarding/step-one');
      return;
    }

    // Load previously selected organization type if it exists
    const savedType = storage.getItem('selectedOrganisationType');
    if (savedType) {
      setSelectedType(savedType);
    }
  }, [navigate]);

  const organisationTypes = [
    { id: 'sole-trader', label: 'Sole Trader', Icon: SoleTraderIcon },
    { id: 'limited-company', label: 'Ltd Company', Icon: SmallBusinessIcon },
    { id: 'enterprise', label: 'Enterprise', Icon: EnterpriseIcon },
    { id: 'charity', label: 'Charity / NGO', Icon: CharityIcon },
  ];

  const handleOrganisationTypeSelect = async (organisationType) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/onboarding/step-two`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          organizationType: organisationType.id
        })
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to validate organization type');
      }

      setSelectedType(organisationType.id);
      storage.setItem('selectedOrganisationType', organisationType.id);
    } catch (error) {
      console.error('Error validating organization type:', error);
      alert('There was an error selecting the organization type. Please try again.');
    }
  };

  const handleContinue = async () => {
    if (!selectedType) {
      alert('Please select an organisation type to continue');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/onboarding/step-two/org-type?type=${selectedType}`);
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to verify organization type');
      }

      navigate('/onboarding/step-three');
    } catch (error) {
      console.error('Error verifying organization type:', error);
      alert('There was an error verifying your selection. Please try again.');
    }
  };

  const handleBack = () => {
    setShowWarning(true);
  };

  const handleConfirmBack = () => {
    // Get step1Data before clearing
    const step1Data = JSON.parse(storage.getItem('step1Data') || '{}');
    
    storage.restartOnboarding();
    
    // Restore step1Data
    if (step1Data) {
      storage.setItem('step1Data', JSON.stringify(step1Data));
    }
    
    navigate('/onboarding/step-one');
  };

  return (
    <>
      <OnboardingLayout
        title="Choose your"
        brandText="organisation type"
        onBack={handleBack}
      >
        <FormContainer>
          <OrganisationTypeContainer>
            {organisationTypes.map((type) => (
              <OrganisationTypeBox
                key={type.id}
                selected={selectedType === type.id}
                onClick={() => handleOrganisationTypeSelect(type)}
              >
                <IconContainer>
                  <IconWrapper>
                    <type.Icon />
                  </IconWrapper>
                  <OrganisationLabel>{type.label}</OrganisationLabel>
                </IconContainer>
                <CheckIconWrapper visible={selectedType === type.id}>
                  <CheckIcon />
                </CheckIconWrapper>
              </OrganisationTypeBox>
            ))}
          </OrganisationTypeContainer>
          <ButtonContainer>
            <PrimaryButton onClick={handleContinue} disabled={!selectedType}>
              Continue
            </PrimaryButton>
          </ButtonContainer>
        </FormContainer>
      </OnboardingLayout>

      {showWarning && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Are you sure?</ModalTitle>
            <ModalText>
              Going back will clear all your progress and start the form again from the beginning. 
              This action cannot be undone.
            </ModalText>
            <ModalButtons>
              <SecondaryButton onClick={() => setShowWarning(false)}>
                Cancel
              </SecondaryButton>
              <WarningButton onClick={handleConfirmBack}>
                Start Over
              </WarningButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default OnboardingStep2; 